import React, { Component } from 'react'
import { connect } from 'react-redux'
import compose from 'lodash/fp/compose'
import isString from 'lodash/isString'

import errorBoundary, {
  ErrorBoundaryProps,
} from 'client/shared/decorators/error-boundary'

import { getAllProducts } from 'client/bookmate/selectors/subscription-selectors'
import { show } from 'client/shared/reducers/fake-page-reducer'

import HeaderBox from 'client/shared/boxes/header-box'

import Subscription from 'client/bookmate/blocks/subscription'
import SubscriptionFaq from 'client/bookmate/blocks/subscription-faq'
import Footer from 'client/shared/blocks/footer'

import {
  CurrentUserSubscriptions,
  Product,
} from 'client/shared/types/subscription'
import { Translations } from 'client/shared/reducers/languages-reducer'
import { CurrentUserState } from 'client/shared/types/current-user'
import { Dispatch, State } from 'shared/types/redux'
import { Location } from 'client/shared/types/react-router'

import { SubscriptionContext } from 'client/shared/helpers/subscription-helper'
import { loadUserSubscriptions } from 'client/bookmate/reducers/subscription-reducer'

type Props = {
  translations: Translations
  products: Product[]
  currentUser: CurrentUserState
  location: Location
  domain: string
  userSubscriptions: CurrentUserSubscriptions
} & { dispatch: Dispatch } & ErrorBoundaryProps

class SubscriptionIndexBox extends Component<Props> {
  componentDidMount() {
    this.props.dispatch(loadUserSubscriptions())
  }

  getSubscriptionFaqKeys() {
    const {
      translations,
      currentUser: {
        data: { locale },
      },
    } = this.props

    // in dev environment, translation strings are in translations[locale] object;
    // in other environments, they are in translations[locale].messages object
    const localizationTexts =
      (translations[locale] && translations[locale].messages) ||
      translations[locale]
    if (!Array.isArray(localizationTexts)) return []

    const faqQuestionRegex = /subscription\.faq_question_(\d+)/
    return Object.keys(localizationTexts)
      .filter(key => faqQuestionRegex.test(key))
      .map(key => {
        const questionNumber = faqQuestionRegex.exec(key)[1]
        const answerKey = `subscription.faq_answer_${questionNumber}`
        return {
          questionId: key,
          answerId: answerKey,
        }
      })
  }

  render(): JSX.Element {
    const {
      currentUser,
      products,
      dispatch,
      location: { query },
      domain,
      userSubscriptions,
    } = this.props

    let contextValue = 'plans'

    if (isString(query.sign_in_token)) {
      contextValue = 'app'
    } else if (isString(query.path)) {
      contextValue = query.path
    }

    return (
      <SubscriptionContext.Provider value={contextValue}>
        <HeaderBox />
        <Subscription
          products={products}
          user={currentUser}
          userSubscriptions={userSubscriptions}
        />
        <SubscriptionFaq
          query={query}
          locKeys={this.getSubscriptionFaqKeys()}
          user={currentUser}
          showGetTheApp={() => dispatch(show('getTheApp', { type: 'book' }))}
          domain={domain}
        />
        <Footer />
      </SubscriptionContext.Provider>
    )
  }
}

const connectWrapper = connect((state: State) => ({
  translations: state.languages.translations,
  products: getAllProducts(state),
  currentUser: state.currentUser,
  domain: state.app.domain,
  userSubscriptions: state.subscription.userSubscriptions,
}))

const wrappers = compose(connectWrapper, errorBoundary)

export default wrappers(SubscriptionIndexBox)
